import { getProductImageSequence } from "@/helpers/imageSequences.js";
import { getFeatureToggles } from "@/helpers/featureToggles.js";

const getFirstReference = (references) => {
  if (!references) return null;
  return references.map((reference) => reference.node)[0];
};

// Later tiles are 240px wide, but the first one is already on the product
// card — keep its original sources so the browser can reuse that cached file
// instead of fetching a new size. Videos keep a poster so a still paints
// while the file buffers.
const toTileImage = (image, product, preferCached) => {
  if (image.sources?.length) {
    const previewUrl = image.previewImage?.url;
    return {
      ...image,
      previewImages: previewUrl ? [{ url: previewUrl }] : [],
    };
  }

  if (preferCached) return image;

  return {
    id: image.id,
    altText: image.altText || product?.title,
    smallMedia:
      image.miniProduct ||
      image.mediumMedia ||
      image.smallMedia ||
      image.largeMedia,
  };
};

/** Media shown in the Add to Bag slide-in carousel, in sequence order. */
export function getMiniProductImages(product) {
  if (!product) return [];

  const { useAltCampaignImages = false } = getFeatureToggles();
  const imageSequence = getProductImageSequence(
    useAltCampaignImages,
    false,
    product
  );

  let usedAltCampaign = false;

  return imageSequence
    .map((sequence) => {
      let mediaItem;

      if (sequence.includes("Video")) {
        // Video metafields can also hold a still image reference
        const reference = getFirstReference(
          product[sequence]?.references.edges
        );
        mediaItem = reference?.sources?.length ? reference : reference?.image;
      } else if (sequence === "altCampaignImages") {
        mediaItem = product.altCampaignImages?.reference?.image;
        if (mediaItem) {
          usedAltCampaign = true;
        }
      } else if (
        sequence === "campaignImages" ||
        sequence === "campaignSecondaryImages"
      ) {
        if (!usedAltCampaign) {
          mediaItem = getFirstReference(
            product[sequence]?.references.edges
          )?.image;
        }
      } else {
        mediaItem = getFirstReference(
          product[sequence]?.references.edges
        )?.image;
      }

      return mediaItem || null;
    })
    .filter(Boolean)
    .filter((mediaItem, index, list) => {
      // Duplicate keys leave tiles stuck on their placeholder
      return list.findIndex((item) => item.id === mediaItem.id) === index;
    })
    .map((mediaItem, index) => toTileImage(mediaItem, product, index === 0));
}

/**
 * Warm the carousel images so the slide-in paints immediately when opened.
 * Safe to call repeatedly — the browser serves already-requested URLs
 * from cache.
 */
export function prefetchMiniProductImages(product) {
  if (typeof window === "undefined") return;

  getMiniProductImages(product).forEach((image) => {
    const url = image.sources?.length
      ? image.previewImages?.[0]?.url
      : image.smallMedia || image.mediumMedia || image.largeMedia;
    if (!url) return;

    const loader = new window.Image();
    loader.src = url;
  });
}
